// components/ChatInterface.tsx
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Mic,
  MicOff,
  Send,
  Trash2,
  Volume2,
  VolumeX,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import VoiceAIService from "../services/VoiceAIService";
import { useAppStore } from "../store/useAppStore";
import MessageBubble from "./message-bubble";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Input,
  Tooltip,
} from "./ui";
import VoiceVisualizer from "./voice-visualizer";

const ChatInterface: React.FC = () => {
  // Use individual selectors to prevent unnecessary re-renders
  const messages = useAppStore((state) => state.messages);
  const currentMessage = useAppStore((state) => state.currentMessage);
  const voiceState = useAppStore((state) => state.voiceState);
  const isLoading = useAppStore((state) => state.isLoading);
  const liveAPIConnected = useAppStore((state) => state.liveAPIConnected);
  const settings = useAppStore((state) => state.settings);
  const aiProvider = useAppStore((state) => state.aiProvider);
  const apiConfig = useAppStore((state) => state.apiConfig);

  // Get actions directly from store
  const addMessage = useAppStore((state) => state.addMessage);
  const setCurrentMessage = useAppStore((state) => state.setCurrentMessage);
  const setVoiceState = useAppStore((state) => state.setVoiceState);
  const setLoading = useAppStore((state) => state.setLoading);
  const clearMessages = useAppStore((state) => state.clearMessages);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const voiceServiceRef = useRef<VoiceAIService | null>(null);
  const audioAnalyzerRef = useRef<AnalyserNode | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state
  const [retryingMessage, setRetryingMessage] = useState<string | null>(null);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);

  // Auto-clear loading state after a timeout (safety net)
  useEffect(() => {
    if (isLoading) {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      // Set a new timeout to force clear loading after 30 seconds
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn("Loading state timeout - forcing clear");
        setLoading(false);
      }, 30000);
    } else {
      // Clear timeout when loading stops
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isLoading, setLoading]);

  // Initialize voice service with proper dependencies
  useEffect(() => {
    voiceServiceRef.current = new VoiceAIService({
      apiConfig,
      provider: aiProvider,
    });

    // Setup Live API event listeners if using Gemini
    if (aiProvider === "gemini" && voiceServiceRef.current) {
      const liveAPI = voiceServiceRef.current.getLiveAPIClient();
      if (liveAPI) {
        // Text response handler - CRITICAL: Clear loading here
        liveAPI.on("textResponse", (text: string) => {
          console.log("Live API response received, clearing loading state");
          setLoading(false);

          addMessage({
            text,
            isUser: false,
            status: "sent",
          });
        });

        // Turn complete handler - backup to ensure loading is cleared
        liveAPI.on("turnComplete", () => {
          console.log("Live API turn complete, ensuring loading is cleared");
          setLoading(false);
        });

        // Error handler
        liveAPI.on("error", (error) => {
          console.error("Live API error, clearing loading state:", error);
          setLoading(false);
        });

        // Connected handler
        liveAPI.on("connected", () => {
          console.log("Live API connected, clearing any loading state");
          setLoading(false);
        });

        // Audio response handler - FIXED: Handle Live API audio properly
        liveAPI.on(
          "audioResponse",
          (audioData: { data: string; mimeType: string }) => {
            console.log("Live API audio response received");

            if (audioData && audioData.data && !voiceState.isMuted) {
              setVoiceState({ isPlaying: true });
              voiceServiceRef.current?.playAudioFromBase64(
                audioData.data,
                audioData.mimeType
              );

              // Clear playing state after a reasonable time
              setTimeout(() => {
                setVoiceState({ isPlaying: false });
              }, 3000);
            }
          }
        );
      }
    }

    return () => {
      voiceServiceRef.current?.cleanup();
    };
  }, [
    aiProvider,
    apiConfig,
    addMessage,
    setLoading,
    voiceState.isMuted,
    setVoiceState,
  ]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Clear loading when new messages are added (additional safety)
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage.isUser && lastMessage.status === "sent") {
        console.log("New AI message detected, clearing loading state");
        setLoading(false);
      }
    }
  }, [messages, setLoading]);

  // Check API configuration
  const hasValidAPIConfig = useCallback(() => {
    if (aiProvider === "openai") {
      return !!apiConfig.openai.apiKey;
    } else {
      return !!apiConfig.gemini.apiKey;
    }
  }, [aiProvider, apiConfig.openai.apiKey, apiConfig.gemini.apiKey]);

  // FIXED: Determine if voice synthesis should be used
  const shouldUseVoiceSynthesis = useCallback(() => {
    return (
      settings.autoSpeak &&
      !voiceState.isMuted &&
      !liveAPIConnected && // Don't use Web Speech API when Live API is connected
      voiceServiceRef.current &&
      !voiceServiceRef.current.isUsingLiveAPI()
    );
  }, [settings.autoSpeak, voiceState.isMuted, liveAPIConnected]);

  // Send message with improved loading management
  const sendMessage = useCallback(
    async (text: string = currentMessage) => {
      if (!text.trim()) return;

      if (!hasValidAPIConfig()) {
        toast.error("API Configuration Required", {
          description: `Please configure your ${aiProvider.toUpperCase()} API key in environment variables.`,
        });
        return;
      }

      console.log("Sending message:", text);

      // Add user message first
      addMessage({
        text: text.trim(),
        isUser: true,
        status: "sent",
      });

      setCurrentMessage("");

      // Determine if we should show loading indicator
      const shouldShowLoading = !liveAPIConnected;

      if (shouldShowLoading) {
        console.log("Setting loading state to true");
        setLoading(true);
      }

      try {
        if (voiceServiceRef.current) {
          const response = await voiceServiceRef.current.sendTextMessage(text);

          // Handle response for non-Live API calls
          if (response && response.trim()) {
            console.log("Received response, clearing loading state");
            setLoading(false);

            addMessage({
              text: response,
              isUser: false,
              status: "sent",
            });

            // FIXED: Generate voice response only when appropriate
            if (shouldUseVoiceSynthesis()) {
              console.log("Generating voice response using Web Speech API");
              setVoiceState({ isPlaying: true });

              try {
                await voiceServiceRef.current.generateVoiceResponse(response);
              } catch (error) {
                console.error("Voice generation error:", error);
              } finally {
                setVoiceState({ isPlaying: false });
              }
            } else if (liveAPIConnected) {
              console.log(
                "Live API connected - voice response handled natively"
              );
            }
          } else if (liveAPIConnected) {
            console.log(
              "Message sent to Live API, response will come via events"
            );
            // Don't clear loading here - let the event handlers do it
          } else {
            // No response and not using Live API
            console.log("No response received, clearing loading state");
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Send message error:", error);
        setLoading(false);

        addMessage({
          text: "Sorry, I encountered an error. Please try again.",
          isUser: false,
          status: "error",
        });

        toast.error("AI Response Error", {
          description:
            error instanceof Error
              ? error.message
              : "Failed to get AI response",
        });
      }
    },
    [
      currentMessage,
      hasValidAPIConfig,
      aiProvider,
      addMessage,
      setCurrentMessage,
      setLoading,
      shouldUseVoiceSynthesis,
      liveAPIConnected,
      setVoiceState,
    ]
  );

  // FIXED: Handle voice recording with Live API awareness
  const toggleRecording = useCallback(async () => {
    if (!voiceState.isRecording) {
      try {
        const stream = await voiceServiceRef.current?.setupVoiceDetection();
        if (!stream) {
          throw new Error("Could not access microphone");
        }

        audioStreamRef.current = stream;
        audioAnalyzerRef.current =
          voiceServiceRef.current?.createAudioAnalyzer(stream) || null;

        const mediaRecorder = voiceServiceRef.current?.createMediaRecorder(
          stream,
          async (audioBlob) => {
            if (voiceServiceRef.current) {
              try {
                console.log("Processing voice input");

                // Only show loading for non-Live API scenarios
                if (!voiceServiceRef.current.isUsingLiveAPI()) {
                  setLoading(true);
                }

                const transcription =
                  await voiceServiceRef.current.processVoiceInput(audioBlob);

                console.log("Voice transcription complete");

                // Only clear loading for non-Live API scenarios
                if (!voiceServiceRef.current.isUsingLiveAPI()) {
                  setLoading(false);
                }

                // Only send message for non-Live API scenarios (Live API handles it directly)
                if (
                  transcription.trim() &&
                  !voiceServiceRef.current.isUsingLiveAPI()
                ) {
                  await sendMessage(transcription);
                }
              } catch (error) {
                console.error("Voice processing error:", error);
                setLoading(false);
                toast.error("Voice Input Error", {
                  description:
                    "Failed to process voice input. Please try again.",
                });
              }
            }
          }
        );

        if (mediaRecorder) {
          mediaRecorderRef.current = mediaRecorder;
          mediaRecorder.start();
          setVoiceState({ isRecording: true, isConnected: true });

          if (audioAnalyzerRef.current) {
            const updateAudioLevels = () => {
              if (audioAnalyzerRef.current && voiceState.isRecording) {
                const levels =
                  voiceServiceRef.current?.getAudioLevels(
                    audioAnalyzerRef.current
                  ) || [];
                setVoiceState({ waveform: levels });
                requestAnimationFrame(updateAudioLevels);
              }
            };
            updateAudioLevels();
          }
        }
      } catch (error) {
        console.error("Error accessing microphone:", error);
        toast.error("Microphone Access Denied", {
          description: "Please check your browser permissions and try again.",
        });
      }
    } else {
      mediaRecorderRef.current?.stop();
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
      setVoiceState({ isRecording: false, isConnected: false, waveform: [] });
    }
  }, [voiceState.isRecording, setVoiceState, setLoading, sendMessage]);

  // Retry failed message
  const retryMessage = useCallback(
    async (messageText: string) => {
      setRetryingMessage(messageText);
      await sendMessage(messageText);
      setRetryingMessage(null);
    },
    [sendMessage]
  );

  // Toggle mute
  const toggleMute = useCallback(() => {
    const newMutedState = !voiceState.isMuted;
    setVoiceState({ isMuted: newMutedState });

    if (!newMutedState && voiceServiceRef.current) {
      voiceServiceRef.current.stopSpeech();
      setVoiceState({ isPlaying: false });
      setPlayingMessageId(null);
    }
  }, [voiceState.isMuted, setVoiceState]);

  // Play message audio
  const playMessageAudio = useCallback(
    async (messageId: string, text: string) => {
      if (!voiceServiceRef.current || voiceState.isMuted) return;

      // Stop any currently playing audio
      if (playingMessageId) {
        voiceServiceRef.current.stopSpeech();
        setVoiceState({ isPlaying: false });
      }

      // Don't use voice synthesis if Live API is connected (it handles audio natively)
      if (voiceServiceRef.current.isUsingLiveAPI()) {
        toast.info("Live API Mode", {
          description: "Voice responses are handled natively by Live API",
        });
        return;
      }

      try {
        setPlayingMessageId(messageId);
        setVoiceState({ isPlaying: true });

        console.log("Playing message audio:", text);
        await voiceServiceRef.current.generateVoiceResponse(text);
      } catch (error) {
        console.error("Error playing message audio:", error);
        toast.error("Voice Playback Error", {
          description: "Failed to play voice response",
        });
      } finally {
        setPlayingMessageId(null);
        setVoiceState({ isPlaying: false });
      }
    },
    [voiceState.isMuted, playingMessageId, setVoiceState]
  );

  // Stop message audio
  const stopMessageAudio = useCallback(() => {
    if (voiceServiceRef.current && playingMessageId) {
      voiceServiceRef.current.stopSpeech();
      setVoiceState({ isPlaying: false });
      setPlayingMessageId(null);
    }
  }, [playingMessageId, setVoiceState]);

  // Handle enter key
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  // Handle clear messages
  const handleClearMessages = useCallback(() => {
    console.log("Clearing messages and loading state");
    clearMessages();
    setLoading(false);
    toast.success("Conversation cleared");
  }, [clearMessages, setLoading]);

  // Force clear loading when Live API state changes
  useEffect(() => {
    console.log("Live API connection changed:", liveAPIConnected);
    if (liveAPIConnected) {
      setLoading(false);
    }
  }, [liveAPIConnected, setLoading]);

  return (
    <Card className="w-full max-w-4xl mx-auto shadow-2xl">
      {/* Chat Header */}
      <CardHeader className="border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <motion.div
              animate={{ rotate: isLoading ? 360 : 0 }}
              transition={{
                duration: 2,
                repeat: isLoading ? Infinity : 0,
                ease: "linear",
              }}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center"
            >
              <span className="text-white font-bold text-sm">AI</span>
            </motion.div>

            <div>
              <h3 className="text-lg font-semibold text-white">
                {aiProvider === "openai"
                  ? "OpenAI Assistant"
                  : "Gemini Assistant"}
              </h3>
              <p className="text-sm text-slate-300">
                {liveAPIConnected ? "Live API • Real-time" : "Standard API"}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Voice playing indicator - FIXED: Only show when actually playing */}
            {voiceState.isPlaying && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center space-x-2 text-green-400"
              >
                <VoiceVisualizer isActive={true} variant="circle" size="sm" />
                <span className="text-xs">
                  {liveAPIConnected ? "Live Audio" : "Speaking"}
                </span>
              </motion.div>
            )}

            {/* Live API indicator */}
            {liveAPIConnected && (
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex items-center space-x-1 text-green-400"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                <span className="text-xs font-medium">LIVE</span>
              </motion.div>
            )}

            {/* Clear messages */}
            <Tooltip content="Clear conversation">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearMessages}
                disabled={messages.length === 0}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </CardHeader>

      {/* Messages Area */}
      <CardContent className="p-0">
        <div
          ref={messagesContainerRef}
          id="messages-container"
          className="h-96 overflow-y-auto p-6 bg-gradient-to-b from-slate-900/50 to-slate-800/50 backdrop-blur-sm"
        >
          {messages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Mic className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Start a conversation
              </h3>
              <p className="text-slate-400 mb-4">
                Type a message or use voice input to begin
              </p>

              {liveAPIConnected ? (
                <div className="text-sm space-y-2">
                  <div className="text-green-400 flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span>Live API ready for real-time conversation</span>
                  </div>
                  <p className="text-xs text-blue-400">
                    🎙️ Native audio • Interruption support • Real-time responses
                  </p>
                </div>
              ) : (
                aiProvider === "gemini" &&
                hasValidAPIConfig() && (
                  <p className="text-xs text-blue-400">
                    💡 Connect to Live API for real-time voice conversations
                  </p>
                )
              )}
            </motion.div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {messages.map((message, index) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    index={index}
                    onRetry={() => retryMessage(message.text)}
                    onPlayAudio={() =>
                      playMessageAudio(message.id, message.text)
                    }
                    onStopAudio={stopMessageAudio}
                    isPlaying={playingMessageId === message.id}
                    canPlayAudio={!voiceState.isMuted && !liveAPIConnected}
                  />
                ))}
              </AnimatePresence>

              {/* Loading indicator - only show when actually loading */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex justify-start"
                >
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-2xl border border-white/20">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            animate={{ y: [0, -4, 0] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: i * 0.1,
                            }}
                            className="w-2 h-2 bg-blue-400 rounded-full"
                          />
                        ))}
                      </div>
                      <span className="text-sm text-slate-300">
                        {retryingMessage
                          ? "Retrying..."
                          : voiceState.isRecording
                          ? "Processing voice..."
                          : liveAPIConnected
                          ? "Live API processing..."
                          : "AI is thinking..."}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </CardContent>

      {/* Input Area */}
      <CardFooter className="border-t border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="w-full space-y-4">
          {/* Voice Recording Indicator */}
          {voiceState.isRecording && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-center"
            >
              <div className="flex items-center justify-center space-x-4 p-3 bg-red-500/20 rounded-lg border border-red-400/30">
                <VoiceVisualizer
                  isActive={voiceState.isRecording}
                  isRecording={true}
                  waveform={voiceState.waveform}
                  variant="bars"
                  size="sm"
                />
                <div className="text-sm">
                  <span className="text-red-400 font-medium">
                    {liveAPIConnected
                      ? "Streaming to Live API..."
                      : "Listening..."}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">
                    Click the microphone again to stop
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Input Controls */}
          <div className="flex items-center space-x-3">
            <Input
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                liveAPIConnected
                  ? "Type or speak your message..."
                  : "Type your message..."
              }
              className="flex-1"
              disabled={isLoading || voiceState.isRecording}
            />

            {/* Send Button */}
            <Tooltip content="Send message">
              <Button
                onClick={() => sendMessage()}
                size="icon"
                disabled={
                  isLoading || !currentMessage.trim() || voiceState.isRecording
                }
                isLoading={isLoading}
              >
                <Send className="h-4 w-4" />
              </Button>
            </Tooltip>

            {/* Voice Button */}
            <Tooltip
              content={
                voiceState.isRecording
                  ? "Stop recording"
                  : liveAPIConnected
                  ? "Real-time voice input"
                  : "Voice input"
              }
            >
              <Button
                onClick={toggleRecording}
                size="icon"
                variant={voiceState.isRecording ? "destructive" : "default"}
                className={`${
                  voiceState.isRecording
                    ? "animate-pulse"
                    : liveAPIConnected
                    ? "ring-2 ring-green-400/30 bg-green-600 hover:bg-green-700"
                    : ""
                }`}
                disabled={isLoading && !voiceState.isRecording}
              >
                {voiceState.isRecording ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            </Tooltip>

            {/* Mute Button - FIXED: Only show when relevant */}
            {(settings.autoSpeak || liveAPIConnected) && (
              <Tooltip
                content={
                  voiceState.isMuted
                    ? "Enable voice responses"
                    : "Mute voice responses"
                }
              >
                <Button onClick={toggleMute} size="icon" variant="outline">
                  {voiceState.isMuted ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Status Info */}
          <div className="text-center">
            <p className="text-xs text-slate-500">
              {!hasValidAPIConfig() ? (
                <span className="text-orange-400 flex items-center justify-center space-x-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>API configuration required</span>
                </span>
              ) : liveAPIConnected ? (
                <span className="text-green-400">
                  🎙️ Live API: Real-time voice conversations with native audio
                </span>
              ) : (
                <span>
                  💬 Standard API:{" "}
                  {aiProvider === "gemini"
                    ? "Connect Live API for real-time voice"
                    : "Voice input with Web Speech API"}
                </span>
              )}
            </p>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ChatInterface;
